/** The sign-in particle field itself — framework-free.
 *
 *  `initParticleField(canvas, { theme })` takes over a canvas and returns a
 *  cleanup function. Ported from the CinchAI (open-webui) auth page; physics
 *  constants, per-theme behaviour and the Star Wars set piece are unchanged.
 *
 *  Differences from the Svelte original, all deliberate:
 *    • Sizes off the canvas box (`bounds: "element"`) or the viewport, instead
 *      of always assuming a full-window fixed canvas — that's what lets the
 *      Design System preview it in a card.
 *    • Never restyles page DOM. Theme backgrounds (OG pattern, Spark gradient)
 *      are a layer the React wrapper renders, so there's nothing to restore.
 *    • Honours prefers-reduced-motion by painting one static frame.
 *
 *  .ts, not .tsx — artwork hex, which the DS lint rule bans in .tsx.
 */

import * as sprite from "./sprites";
import { CINCH_CONFETTI_COLORS, JULY4_COLORS, getThemeColors, type ThemeName } from "./themes";

export interface ParticleFieldOptions {
  theme?: ThemeName;
  /** "viewport" = full-window fixed canvas (the sign-in screen).
   *  "element" = canvas fills its own box (Design System preview). */
  bounds?: "viewport" | "element";
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  orbitDir: number;
  rotation: number;
  rotationSpeed: number;
  color: string;
  life: number;
  isEphemeral: boolean;
  // Star Wars ship fields
  shipType?: number; // 0=Falcon 1=Vader 2=XWing 3=YWing 4=AWing 5=TIE
  alive?: boolean;
  exploding?: boolean;
  explodeTimer?: number;
  respawnDelay?: number;
  fireTimer?: number; // frames until next laser shot
}

interface Flower {
  x: number;
  y: number;
  targetY: number;
  vx: number;
  vy: number;
  emoji: string;
  size: number;
  life: number;
  settled: boolean;
}

interface SparkParticle {
  u: number;
  v: number;
  uVel: number;
  vVel: number;
  noiseOffset: number;
  brightness: number;
}

interface ExplosionDebris {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  color: string;
}

interface LaserBolt {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  r: number;
  g: number;
  b: number;
}

const GRAVITY = 0.0525;
const DAMPING = 0.975;
const MAX_CONN_DIST = 140;
const ORBIT_RADIUS = 120;
const MIN_DIST = 15;

const FLOWER_EMOJIS = ["🌸", "🌺", "🌼", "🌻", "🌷"];
const NEWYEAR_EMOJIS = ["🎊", "🎉", "✨", "🌟", "🎆"];
const MATRIX_CHARS =
  "アイウエオカキクケコサシスセソ0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ@#$%^&*";

const HYPERSPACE_DURATION = 200; // frames (~3.3s)
const GRID_HOLD = 180; // frames (~3s at 60fps)
const SW_BATTLE_START = 600; // ships orbit peacefully for ~10s before firing
const SPARK_COUNT = 120;

/** Smooth organic noise via layered sines — no external lib needed. */
function smoothNoise(offset: number, t: number): number {
  return (
    Math.sin(offset + t * 0.7) * 0.3 +
    Math.sin(offset * 1.7 + t * 0.4) * 0.2 +
    Math.sin(offset * 0.3 + t * 1.1) * 0.1
  );
}

function pick<T>(list: T[]): T {
  return list[Math.floor(Math.random() * list.length)]!;
}

export function initParticleField(
  canvas: HTMLCanvasElement,
  options: ParticleFieldOptions = {},
): () => void {
  const theme: ThemeName = options.theme ?? "default";
  const bounds = options.bounds ?? "viewport";
  const context2d = canvas.getContext("2d");
  if (!context2d) return () => {};
  // Re-bind non-null: the draw helpers below are hoisted function declarations,
  // so TS can't carry the null-check narrowing into them.
  const ctx: CanvasRenderingContext2D = context2d;

  const measure = () => {
    if (bounds === "viewport") return { w: window.innerWidth, h: window.innerHeight };
    const r = canvas.getBoundingClientRect();
    return { w: Math.max(1, Math.round(r.width)), h: Math.max(1, Math.round(r.height)) };
  };
  const first = measure();
  canvas.width = first.w;
  canvas.height = first.h;

  let mouseX = canvas.width / 2;
  let mouseY = canvas.height / 2;
  let animId = 0;
  let disposed = false;
  // Double-burst lasers fire on a timeout; track them so cleanup can cancel.
  const pendingTimers = new Set<number>();

  function makeParticle(x?: number, y?: number): Particle {
    return {
      x: x ?? Math.random() * canvas.width,
      y: y ?? Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.6,
      vy: (Math.random() - 0.5) * 0.6,
      radius: 1.5 + Math.random() * 1.5,
      orbitDir: Math.random() < 0.5 ? 1 : -1,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.08,
      color: pick(CINCH_CONFETTI_COLORS),
      life: 1,
      isEphemeral: false,
    };
  }

  const count = theme === "matrix" || theme === "spark" ? 0 : theme === "starwars" ? 65 : 80;
  let particles: Particle[] = Array.from({ length: count }, () => makeParticle());

  // ── Spring: flowers that grow from the bottom ────────────────────────────
  let flowers: Flower[] = [];
  let flowerFrame = 0;
  let flowersLaunched = false;
  function makeFlower(): Flower {
    return {
      x: Math.random() * canvas.width,
      y: canvas.height + 40,
      targetY: canvas.height * 0.72 + Math.random() * canvas.height * 0.24,
      vx: 0,
      vy: 0,
      emoji: pick(FLOWER_EMOJIS),
      size: 18 + Math.random() * 18,
      life: 1,
      settled: false,
    };
  }

  // ── Spark: 3D torus plexus state ─────────────────────────────────────────
  const sparkParticles: SparkParticle[] = [];
  let sparkFrame = 0;
  if (theme === "spark") {
    for (let i = 0; i < SPARK_COUNT; i++) {
      sparkParticles.push({
        u: Math.random() * Math.PI * 2,
        v: Math.random() * Math.PI * 2,
        uVel: 0.0018 + Math.random() * 0.0008, // clockwise orbital
        vVel: (Math.random() - 0.5) * 0.0008,
        noiseOffset: Math.random() * 1000,
        brightness: 0.6 + Math.random() * 0.4,
      });
    }
  }

  // ── Star Wars state ──────────────────────────────────────────────────────
  let hyperspaceActive = false;
  let hyperspaceTimer = 0;
  let deathStarExploding = false;
  let deathStarExploded = false;
  let deathStarExplosionTimer = 0;
  let explosionDebris: ExplosionDebris[] = [];
  let swBattleTimer = 0;
  let laserBolts: LaserBolt[] = [];

  function initStarWarsShips() {
    particles.forEach((p, idx) => {
      p.shipType = idx === 0 ? 0 : idx === 1 ? 1 : idx <= 19 ? 2 : idx <= 27 ? 3 : idx <= 33 ? 4 : 5;
      p.alive = true;
      p.exploding = false;
      p.explodeTimer = 0;
      p.respawnDelay = 0;
      p.fireTimer = Math.floor(Math.random() * 100);
    });
  }

  function spawnLaser(p: Particle) {
    // Fire along the ship's facing, with a little spread so volleys look organic
    const spread = (Math.random() - 0.5) * 0.18;
    const angle = p.rotation + spread;
    const speed = 10 + Math.random() * 5;
    const life = 22 + Math.floor(Math.random() * 14);
    // Falcon=yellow  Vader=red  X-Wing=blue  Y-Wing/A-Wing=green  TIE=red
    let lr = 255;
    let lg = 220;
    let lb = 50;
    if (p.shipType === 1 || p.shipType === 5) {
      lr = 255;
      lg = 40;
      lb = 40;
    } else if (p.shipType === 2) {
      lr = 60;
      lg = 140;
      lb = 255;
    } else if (p.shipType === 3 || p.shipType === 4) {
      lr = 50;
      lg = 255;
      lb = 80;
    }
    laserBolts.push({
      x: p.x,
      y: p.y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life,
      maxLife: life,
      r: lr,
      g: lg,
      b: lb,
    });
  }

  function respawnShip(p: Particle) {
    // Re-enter from a random screen edge, heading roughly for the middle
    const edge = Math.floor(Math.random() * 4);
    if (edge === 0) {
      p.x = -40;
      p.y = Math.random() * canvas.height;
    } else if (edge === 1) {
      p.x = canvas.width + 40;
      p.y = Math.random() * canvas.height;
    } else if (edge === 2) {
      p.x = Math.random() * canvas.width;
      p.y = -40;
    } else {
      p.x = Math.random() * canvas.width;
      p.y = canvas.height + 40;
    }
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const dx = cx - p.x + (Math.random() - 0.5) * 200;
    const dy = cy - p.y + (Math.random() - 0.5) * 200;
    const d = Math.sqrt(dx * dx + dy * dy) || 1;
    const speed = 0.5 + Math.random() * 1.0;
    p.vx = (dx / d) * speed;
    p.vy = (dy / d) * speed;
    p.rotation = Math.atan2(p.vy, p.vx);
    p.alive = true;
    p.exploding = false;
    p.explodeTimer = 0;
    p.fireTimer = 60 + Math.floor(Math.random() * 80);
  }

  if (theme === "starwars") initStarWarsShips();

  // ── Cinch Launch: grid alignment state ───────────────────────────────────
  let gridMode = false;
  let gridTimer = 0;
  let gridTargets: { x: number; y: number }[] = [];

  function calcGridTargets(): { x: number; y: number }[] {
    const aspect = canvas.width / canvas.height;
    const n = particles.filter((p) => !p.isEphemeral).length;
    const cols = Math.max(1, Math.round(Math.sqrt(n * aspect)));
    const rows = Math.max(1, Math.ceil(n / cols));
    const cellW = canvas.width / (cols + 1);
    const cellH = canvas.height / (rows + 1);
    const result: { x: number; y: number }[] = [];
    let gi = 0;
    for (const p of particles) {
      if (p.isEphemeral) {
        result.push({ x: p.x, y: p.y });
      } else {
        const col = gi % cols;
        const row = Math.floor(gi / cols);
        result.push({ x: (col + 1) * cellW, y: (row + 1) * cellH });
        gi++;
      }
    }
    return result;
  }

  // ── Matrix state ─────────────────────────────────────────────────────────
  let matrixDrops: number[] = [];
  function initMatrix() {
    const cols = Math.floor(canvas.width / 16);
    matrixDrops = Array.from({ length: cols }, () => Math.random() * -30);
  }
  if (theme === "matrix") initMatrix();

  // ── Event handlers ───────────────────────────────────────────────────────
  const toLocal = (e: MouseEvent) => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onMouseMove = (e: MouseEvent) => {
    const { x, y } = toLocal(e);
    mouseX = x;
    mouseY = y;
  };

  const onResize = () => {
    const s = measure();
    canvas.width = s.w;
    canvas.height = s.h;
    if (theme === "matrix") initMatrix();
  };

  const onBangClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.closest("form") || target.tagName === "INPUT" || target.tagName === "BUTTON") return;
    // Take the mouse position from the click itself, so a click that arrives
    // without a preceding mousemove (touch, first interaction) still lands
    // where the user tapped rather than at the centre.
    const { x, y } = toLocal(e);
    if (bounds === "element" && (x < 0 || y < 0 || x > canvas.width || y > canvas.height)) return;
    mouseX = x;
    mouseY = y;

    if (theme === "newyear") {
      // Confetti cannon — mostly upward burst from the click point
      for (let i = 0; i < 45; i++) {
        const angle = (Math.random() - 0.5) * Math.PI - Math.PI / 2;
        const speed = 6 + Math.random() * 16;
        const p = makeParticle(mouseX, mouseY);
        p.vx = Math.cos(angle) * speed;
        p.vy = Math.sin(angle) * speed;
        p.isEphemeral = true;
        particles.push(p);
      }
    } else if (theme === "july4") {
      // Firework radial burst
      for (let i = 0; i < 35; i++) {
        const angle = (i / 35) * Math.PI * 2;
        const speed = 5 + Math.random() * 14;
        const p = makeParticle(mouseX, mouseY);
        p.vx = Math.cos(angle) * speed;
        p.vy = Math.sin(angle) * speed;
        p.color = pick(JULY4_COLORS);
        p.isEphemeral = true;
        particles.push(p);
      }
    } else if (theme === "starwars") {
      // First click blows up the Death Star; hyperspace follows automatically.
      if (!deathStarExploding && !deathStarExploded && !hyperspaceActive) {
        deathStarExploding = true;
        deathStarExplosionTimer = 0;
        explosionDebris = [];
        const DEBRIS_COLORS = ["#888899", "#aaaaaa", "#666677", "#ffaa44", "#ff6622", "#ffdd88"];
        for (let di = 0; di < 90; di++) {
          const angle = Math.random() * Math.PI * 2;
          const speed = 2 + Math.random() * 14;
          explosionDebris.push({
            x: mouseX,
            y: mouseY,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            life: 1,
            color: pick(DEBRIS_COLORS),
          });
        }
        // TIE fighters (index 34+) scatter away in panic
        for (let pi = 34; pi < particles.length; pi++) {
          const p = particles[pi]!;
          const dx = p.x - mouseX;
          const dy = p.y - mouseY;
          const d = Math.sqrt(dx * dx + dy * dy) || 1;
          const speed = 5 + Math.random() * 7;
          p.vx = (dx / d) * speed + (Math.random() - 0.5) * 4;
          p.vy = (dy / d) * speed + (Math.random() - 0.5) * 4;
        }
      }
    } else if (theme === "spring") {
      // Launch all flowers upward and splash rain outward
      flowersLaunched = true;
      for (const f of flowers) {
        f.vy = -(7 + Math.random() * 10);
        f.vx = (Math.random() - 0.5) * 10;
      }
      for (const p of particles) {
        const dx = p.x - mouseX;
        const dy = p.y - mouseY;
        const d = Math.sqrt(dx * dx + dy * dy) || 1;
        p.vx += (dx / d) * (8 + Math.random() * 10);
        p.vy += (dy / d) * (8 + Math.random() * 10);
      }
    } else if (theme === "cinchlaunch") {
      // Snap all tiles into a clean grid
      if (!gridMode) {
        gridMode = true;
        gridTimer = 0;
        gridTargets = calcGridTargets();
      }
    } else if (theme === "snow" || theme === "christmas" || theme === "spark") {
      // No click interaction — keep the peace
    } else {
      // Default big bang
      for (const p of particles) {
        const dx = p.x - mouseX;
        const dy = p.y - mouseY;
        const d = Math.sqrt(dx * dx + dy * dy) || 1;
        const power = 18 + Math.random() * 14;
        p.vx += (dx / d) * power;
        p.vy += (dy / d) * power;
      }
    }
  };

  const isDark = () => document.documentElement.classList.contains("dark");

  // ── Matrix render path (completely separate) ──────────────────────────────
  function drawMatrix() {
    ctx.fillStyle = "rgba(0,0,0,0.06)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Mouse spotlight — radial glow at cursor position
    const glowRange = 130;
    const grad = ctx.createRadialGradient(mouseX, mouseY, 0, mouseX, mouseY, glowRange);
    grad.addColorStop(0, "rgba(0,255,65,0.18)");
    grad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(mouseX - glowRange, mouseY - glowRange, glowRange * 2, glowRange * 2);

    ctx.font = "14px monospace";
    for (let i = 0; i < matrixDrops.length; i++) {
      const drop = matrixDrops[i]!;
      const colX = i * 16;
      const y = drop * 16;
      const distFromMouse = Math.sqrt(Math.pow(mouseX - colX, 2) + Math.pow(mouseY - y, 2));
      const glow = Math.max(0, 1 - distFromMouse / glowRange);

      if (y > 0) {
        const char = pick(MATRIX_CHARS.split(""));
        if (glow > 0.1) {
          // Near the cursor, characters flash white and change rapidly
          const bright = Math.floor(glow * 220);
          ctx.fillStyle = `rgb(${bright},255,${bright})`;
          ctx.fillText(char, colX, y);
          ctx.fillStyle = `rgba(255,255,255,${glow * 0.9})`;
          ctx.fillText(char, colX, y);
        } else {
          ctx.fillStyle = "#00FF41";
          ctx.fillText(char, colX, y);
          ctx.fillStyle = "#CCFFCC";
          ctx.fillText(char, colX, y);
        }
      }

      // Columns near the cursor fall faster — creates a ripple wave
      const speedBoost = 1 + glow * 2.5;
      if (y > canvas.height && Math.random() > 0.975) matrixDrops[i] = 0;
      matrixDrops[i] = matrixDrops[i]! + 0.5 * speedBoost;
    }
  }

  // ── Spark render path: 3D torus plexus ───────────────────────────────────
  function drawSpark() {
    // Dark trail fill — low alpha builds up glow over frames
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = sparkFrame === 0 ? "rgb(6,8,20)" : "rgba(6,8,20,0.18)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const t = sparkFrame * 0.012;
    const dim = Math.min(canvas.width, canvas.height);
    const TORUS_R = dim * 0.32; // major radius
    const TORUS_r = TORUS_R * 0.42; // tube radius
    const FOV = 620;
    const CONN_DIST = dim * 0.13; // connection threshold in screen px
    const TILT_X = 0.4; // forward tilt so the hole faces slightly up
    const TILT_Y = sparkFrame * 0.0014; // very slow global Y-axis rotation

    const cosTX = Math.cos(TILT_X);
    const sinTX = Math.sin(TILT_X);
    const cosTY = Math.cos(TILT_Y);
    const sinTY = Math.sin(TILT_Y);

    // ── Project every spark particle into screen space ──
    const proj: { sx: number; sy: number; z: number; scale: number; idx: number }[] = [];
    for (let i = 0; i < sparkParticles.length; i++) {
      const sp = sparkParticles[i]!;
      const nu = sp.u + smoothNoise(sp.noiseOffset, t) * 0.14;
      const nv = sp.v + smoothNoise(sp.noiseOffset + 500, t) * 0.18;

      // Torus surface in local 3D space
      const x3 = (TORUS_R + TORUS_r * Math.cos(nv)) * Math.cos(nu);
      const y3 = (TORUS_R + TORUS_r * Math.cos(nv)) * Math.sin(nu);
      const z3 = TORUS_r * Math.sin(nv);

      // Rotate around X (tilt forward)
      const y3x = y3 * cosTX - z3 * sinTX;
      const z3x = y3 * sinTX + z3 * cosTX;

      // Rotate around Y (slow orbital spin)
      const x3y = x3 * cosTY + z3x * sinTY;
      const z3y = -x3 * sinTY + z3x * cosTY;

      // Perspective divide
      const scale = FOV / (FOV + z3y + TORUS_R * 0.5);
      proj.push({ sx: cx + x3y * scale, sy: cy + y3x * scale, z: z3y, scale, idx: i });
    }

    // Back-to-front so closer particles paint over farther ones
    proj.sort((a, b) => b.z - a.z);

    // ── Plexus connection lines (additive blend) ──
    ctx.globalCompositeOperation = "screen";
    ctx.lineCap = "round";
    for (let a = 0; a < proj.length; a++) {
      const pa = proj[a]!;
      for (let b = a + 1; b < proj.length; b++) {
        const pb = proj[b]!;
        const dx = pa.sx - pb.sx;
        const dy = pa.sy - pb.sy;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d >= CONN_DIST) continue;
        const tDist = 1 - d / CONN_DIST; // 0=far, 1=close
        const depthAlpha = (pa.scale + pb.scale) * 0.5;
        const alpha = tDist * tDist * 0.55 * depthAlpha;
        ctx.beginPath();
        ctx.moveTo(pa.sx, pa.sy);
        ctx.lineTo(pb.sx, pb.sy);
        ctx.strokeStyle = `rgba(140,200,255,${alpha})`;
        ctx.lineWidth = 0.5 + tDist * 0.6;
        ctx.stroke();
      }
    }

    // ── Glowing nodes (back-to-front) ──
    for (const { sx, sy, scale, idx } of proj) {
      const sp = sparkParticles[idx]!;
      const r = Math.max(0.8, scale * 2.8);
      const brightness = sp.brightness * Math.min(1, scale * 1.6);

      // Outer soft glow
      const grd = ctx.createRadialGradient(sx, sy, 0, sx, sy, r * 5.5);
      grd.addColorStop(0, `rgba(255,245,180,${brightness * 0.5})`);
      grd.addColorStop(0.35, `rgba(180,225,255,${brightness * 0.22})`);
      grd.addColorStop(1, "rgba(80,160,255,0)");
      ctx.globalCompositeOperation = "screen";
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(sx, sy, r * 5.5, 0, Math.PI * 2);
      ctx.fill();

      // Bright core dot
      ctx.fillStyle = `rgba(255,255,245,${brightness})`;
      ctx.beginPath();
      ctx.arc(sx, sy, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // ── Advance positions ──
    for (const sp of sparkParticles) {
      sp.u += sp.uVel;
      sp.v += sp.vVel;
    }

    ctx.globalCompositeOperation = "source-over";
    sparkFrame++;
  }

  // ── Star Wars: laser update, draw, collision ─────────────────────────────
  function drawLasers() {
    for (let bi = laserBolts.length - 1; bi >= 0; bi--) {
      const bolt = laserBolts[bi]!;
      bolt.x += bolt.vx;
      bolt.y += bolt.vy;
      bolt.life--;
      if (
        bolt.life <= 0 ||
        bolt.x < -20 ||
        bolt.x > canvas.width + 20 ||
        bolt.y < -20 ||
        bolt.y > canvas.height + 20
      ) {
        laserBolts.splice(bi, 1);
        continue;
      }

      // Collision — only after the orbit phase, and 85% of close bolts miss
      let hit = false;
      if (swBattleTimer >= SW_BATTLE_START) {
        for (const tp of particles) {
          if (!tp.alive || tp.shipType === 0 || tp.shipType === 1) continue;
          if (tp.exploding) continue;
          const dx = bolt.x - tp.x;
          const dy = bolt.y - tp.y;
          if (dx * dx + dy * dy < 16 * 16 && Math.random() > 0.85) {
            tp.alive = false;
            tp.exploding = true;
            tp.explodeTimer = 0;
            laserBolts.splice(bi, 1);
            hit = true;
            break;
          }
        }
      }
      if (hit) continue;

      // Bright glowing line segment
      const lifeRatio = bolt.life / bolt.maxLife;
      const alpha = Math.min(1, lifeRatio * 2.5);
      const boltLen = 10 + (1 - lifeRatio) * 4;
      const tailX = bolt.x - (bolt.vx * boltLen) / 10;
      const tailY = bolt.y - (bolt.vy * boltLen) / 10;
      ctx.save();
      ctx.globalCompositeOperation = "screen";
      // Outer glow
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(bolt.x, bolt.y);
      ctx.strokeStyle = `rgba(${bolt.r},${bolt.g},${bolt.b},${alpha * 0.45})`;
      ctx.lineWidth = 3.5;
      ctx.stroke();
      // Bright core
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(bolt.x, bolt.y);
      ctx.strokeStyle = `rgba(${bolt.r},${bolt.g},${bolt.b},${alpha})`;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.globalCompositeOperation = "source-over";
      ctx.restore();
    }
  }

  // ── Spring: flower accumulation & render ─────────────────────────────────
  function drawFlowers() {
    flowerFrame++;

    // Reset after all launched flowers have faded
    if (flowersLaunched && flowers.every((f) => f.life <= 0)) {
      flowers = [];
      flowersLaunched = false;
      flowerFrame = 0;
    }

    // Add a new flower every ~2.5s until we have 28
    if (!flowersLaunched && flowers.length < 28 && flowerFrame % 150 === 0) {
      flowers.push(makeFlower());
    }

    flowers.forEach((f, fi) => {
      if (f.life <= 0) return;

      if (flowersLaunched) {
        f.vy += 0.35;
        f.vx *= 0.98;
        f.vy *= 0.98;
        f.life -= 0.018;
      } else if (!f.settled) {
        const dy = f.targetY - f.y;
        if (Math.abs(dy) < 1) {
          f.y = f.targetY;
          f.settled = true;
        } else {
          f.y += dy * 0.04;
        }
        f.x += Math.sin(flowerFrame * 0.06 + fi) * 0.4;
      } else {
        // Gentle settled sway
        f.x += Math.sin(flowerFrame * 0.025 + fi * 1.7) * 0.18;
      }

      f.x += f.vx;
      f.y += f.vy;

      ctx.globalAlpha = Math.max(0, f.life);
      sprite.drawEmoji(ctx, f.emoji, f.x, f.y, f.size);
      ctx.globalAlpha = 1;
    });
  }

  // ── Star Wars: Death Star follows the cursor, then detonates ─────────────
  function drawDeathStarLayer() {
    const dsSize = Math.min(canvas.width, canvas.height) * 0.052;

    if (!deathStarExploded && !deathStarExploding) {
      sprite.drawDeathStar(ctx, mouseX, mouseY, dsSize);
    }

    if (deathStarExploding || deathStarExploded) {
      const t = deathStarExplosionTimer;
      // Expanding shockwave rings
      if (t < 90) {
        const ringR = t * 9;
        const ringAlp = Math.max(0, 1 - t / 90);
        ctx.beginPath();
        ctx.arc(mouseX, mouseY, ringR, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,160,60,${ringAlp * 0.85})`;
        ctx.lineWidth = 3.5;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(mouseX, mouseY, ringR * 0.55, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,230,120,${ringAlp * 0.55})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        // Central flash
        if (t < 18) {
          const flashAlp = (1 - t / 18) * 0.9;
          const flashGrd = ctx.createRadialGradient(
            mouseX,
            mouseY,
            0,
            mouseX,
            mouseY,
            dsSize * 2.5,
          );
          flashGrd.addColorStop(0, `rgba(255,255,200,${flashAlp})`);
          flashGrd.addColorStop(1, "rgba(255,140,0,0)");
          ctx.fillStyle = flashGrd;
          ctx.beginPath();
          ctx.arc(mouseX, mouseY, dsSize * 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      // Debris
      for (const db of explosionDebris) {
        db.x += db.vx;
        db.y += db.vy;
        db.vx *= 0.97;
        db.vy *= 0.97;
        db.vy += 0.04;
        db.life -= 0.008;
        if (db.life <= 0) continue;
        ctx.globalAlpha = db.life;
        ctx.fillStyle = db.color;
        ctx.beginPath();
        ctx.arc(db.x, db.y, 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
  }

  // ── Main draw ────────────────────────────────────────────────────────────
  function draw() {
    if (theme === "matrix") {
      drawMatrix();
      return;
    }
    if (theme === "spark") {
      drawSpark();
      return;
    }

    // Tick hyperspace once per frame (not per particle)
    if (theme === "starwars" && hyperspaceActive) {
      hyperspaceTimer++;
      if (hyperspaceTimer >= HYPERSPACE_DURATION) hyperspaceActive = false;
    }

    // Ships orbit peacefully until the battle timer passes
    if (theme === "starwars" && !hyperspaceActive && !deathStarExploding) {
      if (swBattleTimer < SW_BATTLE_START) swBattleTimer++;
    }

    // Death Star explosion auto-launches hyperspace when it finishes
    if (theme === "starwars" && deathStarExploding) {
      deathStarExplosionTimer++;
      if (deathStarExplosionTimer >= 150) {
        deathStarExploding = false;
        deathStarExploded = true;
        hyperspaceActive = true;
        hyperspaceTimer = 0;
        laserBolts = [];
        const cx = canvas.width / 2;
        const cy = canvas.height / 2;
        for (const p of particles) {
          const dx = p.x - cx;
          const dy = p.y - cy;
          const d = Math.sqrt(dx * dx + dy * dy) || 1;
          p.vx = ((28 + Math.random() * 22) * dx) / d;
          p.vy = ((28 + Math.random() * 22) * dy) / d;
        }
      }
    }

    if (theme === "cinchlaunch" && gridMode) {
      gridTimer++;
      if (gridTimer >= GRID_HOLD) {
        gridMode = false;
        gridTargets = [];
      }
    }

    // Hyperspace fills with a dark trail instead of clearing
    if (theme === "starwars" && hyperspaceActive) {
      const fadeIn = Math.min(1, hyperspaceTimer / 25);
      ctx.fillStyle = `rgba(0,0,10,${0.25 + fadeIn * 0.2})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    const { dot: dotColor, line: lineColor } = getThemeColors(theme, isDark());

    // Cull dead ephemeral particles
    if (particles.some((p) => p.isEphemeral && p.life <= 0))
      particles = particles.filter((p) => !p.isEphemeral || p.life > 0);

    for (let i = 0; i < particles.length; i++) {
      const p = particles[i]!;

      // ── Physics ──
      if (p.isEphemeral) {
        p.life -= 0.016;
        p.vy += 0.3; // gravity on burst particles
        p.vx *= 0.98;
        p.vy *= 0.98;
      } else if (theme === "snow") {
        p.vy += 0.015;
        p.vx += (Math.random() - 0.5) * 0.02;
        p.vx *= 0.995;
        p.vy = Math.min(p.vy, 0.7);
        p.rotation += p.rotationSpeed * 0.3;
      } else if (theme === "christmas") {
        // Snowflakes drift down — no mouse tracking
        p.vy += 0.012;
        p.vx += (Math.random() - 0.5) * 0.015;
        p.vx *= 0.995;
        p.vy = Math.min(p.vy, 0.8);
        p.rotation += p.rotationSpeed * 0.4;
      } else if (theme === "starwars" && deathStarExploding && i >= 34) {
        // TIE fighters fleeing the explosion — coast freely, no damping
        p.rotation += p.rotationSpeed * 1.8; // spin with panic
      } else if (theme === "starwars" && hyperspaceActive) {
        // Keep flying outward, no mouse influence (timer ticked above)
      } else if (theme === "spring") {
        // Rain falls straight down, mouse has no gravity pull
        p.vx *= 0.92;
        p.vy += 0.12;
        p.vy = Math.min(p.vy, 6);
      } else if (theme === "cinchlaunch" && gridMode) {
        // Lerp smoothly toward the assigned grid slot
        const gt = gridTargets[i];
        if (gt) {
          p.vx += (gt.x - p.x) * 0.09;
          p.vy += (gt.y - p.y) * 0.09;
        }
        p.vx *= 0.72;
        p.vy *= 0.72;
        p.rotation += p.rotationSpeed * 0.08;
      } else {
        const dx = mouseX - p.x;
        const dy = mouseY - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const rx = dx / dist;
        const ry = dy / dist;
        if (dist < MIN_DIST) {
          p.vx -= rx * 2.5;
          p.vy -= ry * 2.5;
        } else {
          const force = Math.min((GRAVITY * 60) / dist, 1.2);
          p.vx += rx * force;
          p.vy += ry * force;
          const orbitStr = Math.max(0, 1 - dist / ORBIT_RADIUS);
          p.vx += -ry * p.orbitDir * orbitStr;
          p.vy += rx * p.orbitDir * orbitStr;
        }
        p.vx *= DAMPING;
        p.vy *= DAMPING;
        p.rotation += p.rotationSpeed;
      }

      p.x += p.vx;
      p.y += p.vy;

      // ── Edge handling ──
      if (p.isEphemeral) {
        if (p.x < -30 || p.x > canvas.width + 30 || p.y > canvas.height + 30) p.life = 0;
      } else if (theme === "snow" || theme === "christmas") {
        if (p.y > canvas.height) {
          p.y = -10;
          p.x = Math.random() * canvas.width;
          p.vy = 0;
        }
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
      } else if (theme === "starwars" && hyperspaceActive) {
        // Loop particles back through the centre so streaks keep coming
        if (
          p.x < -60 ||
          p.x > canvas.width + 60 ||
          p.y < -60 ||
          p.y > canvas.height + 60
        ) {
          const cx = canvas.width / 2;
          const cy = canvas.height / 2;
          p.x = cx + (Math.random() - 0.5) * 40;
          p.y = cy + (Math.random() - 0.5) * 40;
          const angle = Math.random() * Math.PI * 2;
          const speed = 28 + Math.random() * 22;
          p.vx = Math.cos(angle) * speed;
          p.vy = Math.sin(angle) * speed;
        }
      } else if (theme === "spring") {
        // Rain resets above the screen when it exits the bottom
        if (p.y > canvas.height + 10) {
          p.y = -(5 + Math.random() * 30);
          p.x = Math.random() * canvas.width;
          p.vx = 0;
          p.vy = 2 + Math.random() * 2;
        }
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
      } else {
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
      }

      // ── Draw particle ──
      const alpha = p.isEphemeral ? p.life : 1;
      const sz = p.radius * 2.5;
      ctx.globalAlpha = alpha;

      switch (theme) {
        case "newyear":
          sprite.drawEmoji(
            ctx,
            NEWYEAR_EMOJIS[i % NEWYEAR_EMOJIS.length]!,
            p.x,
            p.y,
            p.radius * 7,
          );
          break;
        case "vday":
          sprite.drawEmoji(ctx, "❤️", p.x, p.y, p.radius * 7);
          break;
        case "stpatricks":
          sprite.drawEmoji(ctx, "🍀", p.x, p.y, p.radius * 7);
          break;
        case "halloween":
          sprite.drawEmoji(ctx, "🦇", p.x, p.y, p.radius * 7);
          break;
        case "spring":
          sprite.drawEmoji(ctx, "💧", p.x, p.y, p.radius * 6);
          break;
        case "starwars":
          if (hyperspaceActive) {
            // Motion-blur streak
            const fadeIn = Math.min(1, hyperspaceTimer / 20);
            ctx.save();
            const grad = ctx.createLinearGradient(
              p.x - p.vx * 0.35,
              p.y - p.vy * 0.35,
              p.x,
              p.y,
            );
            grad.addColorStop(0, "rgba(180,210,255,0)");
            grad.addColorStop(1, `rgba(220,235,255,${0.9 * fadeIn})`);
            ctx.beginPath();
            ctx.moveTo(p.x - p.vx * 0.35, p.y - p.vy * 0.35);
            ctx.lineTo(p.x, p.y);
            ctx.strokeStyle = grad;
            ctx.lineWidth = 1.2 + p.radius * 0.3;
            ctx.stroke();
            ctx.restore();
          } else if (p.alive === false) {
            if (p.exploding) {
              // Debris ring, then a respawn countdown
              p.explodeTimer = (p.explodeTimer ?? 0) + 1;
              const et = p.explodeTimer;
              const ringAlp = Math.max(0, 1 - et / 28);
              ctx.beginPath();
              ctx.arc(p.x, p.y, et * 3.2, 0, Math.PI * 2);
              ctx.strokeStyle = `rgba(255,140,50,${ringAlp * 0.7})`;
              ctx.lineWidth = 2;
              ctx.stroke();
              if (et > 28) {
                p.exploding = false;
                p.respawnDelay = 220 + Math.floor(Math.random() * 120);
              }
            } else {
              p.respawnDelay = (p.respawnDelay ?? 0) - 1;
              if (p.respawnDelay <= 0) respawnShip(p);
            }
          } else {
            // Alive — fire on cooldown
            if (!deathStarExploding && !hyperspaceActive) {
              p.fireTimer = (p.fireTimer ?? 0) - 1;
              if (p.fireTimer <= 0) {
                spawnLaser(p);
                // Some ships fire a double burst
                if (Math.random() < 0.4) {
                  const id = window.setTimeout(() => {
                    pendingTimers.delete(id);
                    if (!disposed) spawnLaser(p);
                  }, 80);
                  pendingTimers.add(id);
                }
                p.fireTimer = 70 + Math.floor(Math.random() * 110);
              }
            }
            const sz6 = p.radius * 6;
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rotation);
            if (p.shipType === 0) sprite.drawMillenniumFalcon(ctx, sz6 * 2.2);
            else if (p.shipType === 1) sprite.drawVaderTIE(ctx, sz6);
            else if (p.shipType === 2) sprite.drawXWing(ctx, sz6);
            else if (p.shipType === 3) sprite.drawYWing(ctx, sz6);
            else if (p.shipType === 4) sprite.drawAWing(ctx, sz6);
            else sprite.drawTIEFighter(ctx, sz6);
            ctx.restore();
          }
          break;
        case "christmas":
        case "snow":
          sprite.drawSnowflake(ctx, p.x, p.y, sz, dotColor, p.rotation);
          break;
        case "july4":
          if (p.isEphemeral) {
            // Burst particles: red/blue stars
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rotation);
            sprite.drawStar(ctx, 0, 0, sz, 0.42);
            ctx.fillStyle = p.color;
            ctx.fill();
            ctx.restore();
          } else {
            sprite.drawFlag(ctx, p.x, p.y, sz);
          }
          break;
        case "cinchlaunch":
          sprite.drawCinchTile(ctx, p.x, p.y, p.radius * 8, p.color);
          break;
        default:
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
          ctx.fillStyle = dotColor;
          ctx.fill();
      }

      ctx.globalAlpha = 1;

      // ── Connections ──
      if (!p.isEphemeral && theme !== "snow" && theme !== "spring" && theme !== "starwars") {
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j]!;
          if (q.isEphemeral) continue;
          const ddx = p.x - q.x;
          const ddy = p.y - q.y;
          const d = Math.sqrt(ddx * ddx + ddy * ddy);
          if (d >= MAX_CONN_DIST) continue;
          const lineAlpha = (1 - d / MAX_CONN_DIST) * 0.4;
          if (theme === "cinchlaunch") {
            // Grey directional arrow — source to target
            sprite.drawArrowLine(ctx, p.x, p.y, q.x, q.y, lineAlpha + 0.15, "rgba(140,150,165,");
          } else if (theme === "vday") {
            sprite.drawArrowLine(ctx, p.x, p.y, q.x, q.y, lineAlpha);
          } else if (theme === "stpatricks") {
            ctx.setLineDash([4, 4]);
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `${lineColor}${lineAlpha})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
            ctx.setLineDash([]);
          } else if (theme === "july4") {
            const lineColors = ["rgba(191,10,48,", "rgba(0,40,104,"];
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `${lineColors[(i + j) % lineColors.length]!}${lineAlpha})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          } else {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `${lineColor}${lineAlpha})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }
    }

    if (theme === "starwars" && !hyperspaceActive) drawLasers();
    if (theme === "spring") drawFlowers();
    if (theme === "starwars" && !hyperspaceActive) drawDeathStarLayer();
  }

  const frame = () => {
    animId = requestAnimationFrame(frame);
    draw();
  };

  const reduced =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const onResizeStatic = () => {
    onResize();
    draw();
  };

  if (reduced) {
    // One static frame — the plexus still decorates the page, but nothing
    // moves, so cursor/click interaction is left off entirely.
    window.addEventListener("resize", onResizeStatic);
    draw();
  } else {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("resize", onResize);
    window.addEventListener("click", onBangClick);
    frame();
  }

  return () => {
    disposed = true;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("resize", onResize);
    window.removeEventListener("resize", onResizeStatic);
    window.removeEventListener("click", onBangClick);
    cancelAnimationFrame(animId);
    for (const id of pendingTimers) window.clearTimeout(id);
    pendingTimers.clear();
  };
}
